/**
 * Strategic Ambient Music Generator
 * 
 * Procedurally generated ambient music for Iron Meridian.
 * Creates evolving, non-repetitive background music suitable for
 * long strategy game sessions.
 * 
 * Musical approach:
 * - Slow-moving harmonic progressions
 * - Generative melody fragments
 * - Atmospheric pads and textures
 * - Occasional rhythmic elements
 */

// =====================================================================
// MUSICAL CONSTANTS
// =====================================================================

// Scale: C minor pentatonic for dark strategic feel
const C_MINOR_PENTATONIC = [
  130.81, // C3
  155.56, // Eb3
  196.00, // G3
  233.08, // Bb3
  261.63, // C4
  311.13, // Eb4
  392.00, // G4
  466.16, // Bb4
  523.25, // C5
];

// Chord progressions (indices into scale)
const PROGRESSIONS = [
  [[0, 2, 4], [1, 3, 5], [2, 4, 6], [0, 2, 4]], // i - III - v - i
  [[0, 2, 4], [3, 5, 7], [1, 3, 5], [0, 2, 4]], // i - VII - III - i
  [[0, 2, 4], [2, 4, 6], [1, 3, 5], [3, 5, 7]], // i - v - III - VII
];

// Timing constants (in seconds)
const CHORD_DURATION = 8;
const MELODY_NOTE_MIN = 0.5;
const MELODY_NOTE_MAX = 2;
const MELODY_REST_CHANCE = 0.3;

// =====================================================================
// STRATEGIC AMBIENT MUSIC
// =====================================================================

/**
 * Create strategic ambient music
 * Returns an object with stop() method to control playback
 */
export function createStrategicMusic(ctx, destination) {
  if (!ctx || !destination) return null;

  const now = ctx.currentTime;
  const nodes = [];
  let isPlaying = true;
  let chordTimeoutId = null;
  let melodyTimeoutId = null;
  let currentProgression = PROGRESSIONS[0];
  let chordIndex = 0;

  // Master gain for fade in/out
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0, now);
  masterGain.gain.linearRampToValueAtTime(0.6, now + 3);
  masterGain.connect(destination);
  nodes.push(masterGain);

  // Pad gain (for the background chords)
  const padGain = ctx.createGain();
  padGain.gain.setValueAtTime(0.4, now);
  padGain.connect(masterGain);
  nodes.push(padGain);

  // Melody gain
  const melodyGain = ctx.createGain();
  melodyGain.gain.setValueAtTime(0.25, now);
  melodyGain.connect(masterGain);
  nodes.push(melodyGain);

  // ---------------------------------------------------------------
  // Layer 1: Evolving pad chords
  // ---------------------------------------------------------------
  let activeChordNodes = [];

  function playChord(chordIndices) {
    // Fade out old chord
    activeChordNodes.forEach(node => {
      try {
        const fadeTime = ctx.currentTime;
        if (node.gain) {
          node.gain.gain.setValueAtTime(node.gain.gain.value, fadeTime);
          node.gain.gain.linearRampToValueAtTime(0, fadeTime + 2);
        }
        setTimeout(() => {
          try {
            if (node.osc) node.osc.stop();
            node.osc?.disconnect();
            node.gain?.disconnect();
          } catch (e) {}
        }, 2500);
      } catch (e) {}
    });
    activeChordNodes = [];

    // Create new chord
    const currentTime = ctx.currentTime;
    
    chordIndices.forEach((scaleIndex, i) => {
      const freq = C_MINOR_PENTATONIC[scaleIndex];
      if (!freq) return;

      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, currentTime);
      
      // Gentle detuning for richness
      if (i > 0) {
        osc.detune.setValueAtTime(randomBetween(-5, 5), currentTime);
      }

      // Gentle filter sweep
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(600, currentTime);
      filter.frequency.linearRampToValueAtTime(1200, currentTime + CHORD_DURATION / 2);
      filter.frequency.linearRampToValueAtTime(600, currentTime + CHORD_DURATION);
      filter.Q.value = 0.5;

      const noteGain = ctx.createGain();
      noteGain.gain.setValueAtTime(0, currentTime);
      noteGain.gain.linearRampToValueAtTime(0.08, currentTime + 1.5);
      noteGain.gain.setValueAtTime(0.08, currentTime + CHORD_DURATION - 2);
      noteGain.gain.linearRampToValueAtTime(0, currentTime + CHORD_DURATION);

      osc.connect(filter);
      filter.connect(noteGain);
      noteGain.connect(padGain);

      osc.start(currentTime);
      osc.stop(currentTime + CHORD_DURATION + 0.1);

      activeChordNodes.push({ osc, gain: noteGain });
    });
  }

  function scheduleNextChord() {
    if (!isPlaying) return;

    const chord = currentProgression[chordIndex];
    playChord(chord);
    
    chordIndex = (chordIndex + 1) % currentProgression.length;
    
    // Occasionally change progression
    if (chordIndex === 0 && Math.random() < 0.3) {
      currentProgression = PROGRESSIONS[Math.floor(Math.random() * PROGRESSIONS.length)];
    }

    chordTimeoutId = setTimeout(() => {
      scheduleNextChord();
    }, CHORD_DURATION * 1000);
  }

  // Start chords
  scheduleNextChord();

  // ---------------------------------------------------------------
  // Layer 2: Generative melody fragments
  // ---------------------------------------------------------------
  let lastMelodyNote = 4; // Start in middle of scale

  function playMelodyNote() {
    if (!isPlaying) return;

    // Sometimes rest
    if (Math.random() < MELODY_REST_CHANCE) {
      scheduleMelodyNote();
      return;
    }

    const currentTime = ctx.currentTime;
    
    // Move to adjacent note most of the time, occasional leap
    let noteIndex;
    if (Math.random() < 0.7) {
      // Step motion
      const direction = Math.random() < 0.5 ? -1 : 1;
      noteIndex = Math.max(0, Math.min(C_MINOR_PENTATONIC.length - 1, lastMelodyNote + direction));
    } else {
      // Leap
      noteIndex = Math.floor(Math.random() * C_MINOR_PENTATONIC.length);
    }
    
    const freq = C_MINOR_PENTATONIC[noteIndex];
    lastMelodyNote = noteIndex;

    const duration = randomBetween(MELODY_NOTE_MIN, MELODY_NOTE_MAX);

    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq * 2, currentTime); // Octave higher

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2000, currentTime);
    filter.Q.value = 1;

    const noteGain = ctx.createGain();
    noteGain.gain.setValueAtTime(0, currentTime);
    noteGain.gain.linearRampToValueAtTime(0.15, currentTime + 0.05);
    noteGain.gain.exponentialRampToValueAtTime(0.001, currentTime + duration);

    osc.connect(filter);
    filter.connect(noteGain);
    noteGain.connect(melodyGain);

    osc.start(currentTime);
    osc.stop(currentTime + duration + 0.1);

    osc.onended = () => {
      osc.disconnect();
      noteGain.disconnect();
    };

    scheduleMelodyNote();
  }

  function scheduleMelodyNote() {
    if (!isPlaying) return;
    
    const delay = randomBetween(MELODY_NOTE_MIN, MELODY_NOTE_MAX) * 1000;
    melodyTimeoutId = setTimeout(playMelodyNote, delay);
  }

  // Start melody after a short delay
  setTimeout(() => {
    if (isPlaying) playMelodyNote();
  }, 2000);

  // ---------------------------------------------------------------
  // Layer 3: Subtle high shimmer
  // ---------------------------------------------------------------
  const shimmerOsc = ctx.createOscillator();
  shimmerOsc.type = 'sine';
  shimmerOsc.frequency.setValueAtTime(C_MINOR_PENTATONIC[8] * 2, now); // High C6

  const shimmerLfo = ctx.createOscillator();
  shimmerLfo.type = 'sine';
  shimmerLfo.frequency.setValueAtTime(0.1, now);

  const shimmerLfoGain = ctx.createGain();
  shimmerLfoGain.gain.setValueAtTime(0.02, now);

  const shimmerGain = ctx.createGain();
  shimmerGain.gain.setValueAtTime(0.015, now);

  shimmerLfo.connect(shimmerLfoGain);
  shimmerLfoGain.connect(shimmerGain.gain);
  shimmerOsc.connect(shimmerGain);
  shimmerGain.connect(masterGain);

  shimmerOsc.start(now);
  shimmerLfo.start(now);
  nodes.push(shimmerOsc, shimmerLfo, shimmerLfoGain, shimmerGain);

  // ---------------------------------------------------------------
  // Return control object
  // ---------------------------------------------------------------
  return {
    stop: () => {
      isPlaying = false;
      
      if (chordTimeoutId) clearTimeout(chordTimeoutId);
      if (melodyTimeoutId) clearTimeout(melodyTimeoutId);

      // Fade out
      const fadeStart = ctx.currentTime;
      masterGain.gain.setValueAtTime(masterGain.gain.value, fadeStart);
      masterGain.gain.linearRampToValueAtTime(0, fadeStart + 2);

      // Stop active chord notes
      activeChordNodes.forEach(node => {
        try {
          const fadeTime = ctx.currentTime;
          if (node.gain) {
            node.gain.gain.setValueAtTime(node.gain.gain.value, fadeTime);
            node.gain.gain.linearRampToValueAtTime(0, fadeTime + 2);
          }
        } catch (e) {}
      });

      // Stop and disconnect after fade
      setTimeout(() => {
        nodes.forEach(node => {
          try {
            if (node.stop) node.stop();
            node.disconnect();
          } catch (e) {}
        });
        activeChordNodes.forEach(node => {
          try {
            if (node.osc) node.osc.stop();
            node.osc?.disconnect();
            node.gain?.disconnect();
          } catch (e) {}
        });
      }, 2500);
    }
  };
}

// =====================================================================
// UTILITY
// =====================================================================

function randomBetween(min, max) {
  return Math.random() * (max - min) + min;
}

export default createStrategicMusic;
